<script>
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import { cashSave, tryFocus } from '@/helpers/utils'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import { createOffer } from '@/api/cars'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import { mapGetters } from 'vuex'
import FeatureToggle from '@/components/cars/edit/FeatureToggle.vue'
import FeatureTextarea from '@/components/cars/edit/FeatureTextarea.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-feature-input': FeatureInput,
    'mia-feature-textarea': FeatureTextarea,
    'mia-feature-toggle': FeatureToggle,
    'mia-notice': Notice,
    'mia-submit-button': SubmitButton
  },

  mixins: [FormattingHelpers, HandlesBackendValidationErrors],

  props: {
    car: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      form: {
        item_price: '',
        item_fullsizeimages: true,
        item_offer_defaulttext: ''
      }
    }
  },

  computed: {
    ...mapGetters(['vatRate']),

    isMissingConsumptionValues() {
      return this.car.item_weight < 1
        || this.car.item_consumption < 0
        || this.car.item_consumption_town < 0
        || this.car.item_consumption_outoftown < 0
        || this.car.item_co2 < 0
    },

    isMissingConfiguration() {
      return !this.car.item_configuration || !Array.isArray(this.car.item_configuration) || this.car.item_configuration.length === 0
    },

    isMissingImages() {
      return this.car.item_files.filter(file => file.shared && /^image\//i.test(file.mime)).length === 0
    },

    isMissingWltpValues() {
      const defaultFields = [
        'item_consumption_combined_wltp',
        'item_wltp_consumption_low',
        'item_wltp_consumption_medium',
        'item_wltp_consumption_high',
        'item_wltp_consumption_highest',
        'item_co2_wltp',
        'item_wltp_20000km_mileage_costs',
        'item_wltp_yearly_tax_costs'
      ]
      const petrolLikeFields = [
        'item_consumption_combined_wltp',
        'item_wltp_consumption_low',
        'item_wltp_consumption_medium',
        'item_wltp_consumption_high',
        'item_wltp_consumption_highest',
        'item_co2_wltp',
        'item_wltp_20000km_mileage_costs',
        'item_wltp_yearly_tax_costs'
      ]
      const hybridFields = [
        'item_consumption_combined_weighted_wltp',
        'item_power_consumption_combined_weighted_wltp',
        'item_wltp_electric_engine_power_kw',
        'item_co2_emission_combined_weighted_wltp',
        'item_electric_range_wltp',
        'item_wltp_electric_range_city',
        'item_wltp_20000km_mileage_costs',
        'item_wltp_yearly_tax_costs'
      ]
      const fieldsMap = {
        PETROL: petrolLikeFields,
        DIESEL: petrolLikeFields,
        LPG: petrolLikeFields,
        ELECTRICITY: [
          'item_power_consumption_wltp',
          'item_electric_range_wltp',
          'item_wltp_electric_range_city',
          'item_wltp_20000km_mileage_costs',
          'item_wltp_yearly_tax_costs'
        ],
        HYBRID: hybridFields,
        HYBRID_DIESEL: hybridFields,
        CNG: [
          'item_consumption_combined_wltp',
          'item_wltp_consumption_medium',
          'item_wltp_consumption_high',
          'item_wltp_consumption_highest',
          'item_co2_wltp',
          'item_wltp_20000km_mileage_costs',
          'item_wltp_yearly_tax_costs'
        ],
        HYDROGENIUM: [
          'item_wltp_hydrogen_consumption_combined',
          'item_wltp_20000km_mileage_costs',
          'item_wltp_yearly_tax_costs'
        ]
      }

      if (!this.car.item_mobilede_data || Object.keys(this.car.item_mobilede_data).length === 0) {
        return true
      }

      const requiredFields = fieldsMap[this.car.item_mobilede_data.fuel] || defaultFields

      return requiredFields.some(field => !this.car[field])
    }
  },

  created() {
    if (!this.car.item_price_customer) {
      return
    }

    if (this.car.item_tax === 'differential') {
      this.$set(this.form, 'item_price', `${this.car.item_price_customer}`.replace('.', ','))
    } else {
      const grossPriceCustomer = parseFloat(`${this.car.item_price_customer}`.replace(',', '.')) * (1 + this.vatRate)

      this.$set(this.form, 'item_price', `${grossPriceCustomer}`.replace('.', ','))
    }
  },

  methods: {
    cashSave,

    onOpened() {
      tryFocus(this.$refs.priceInputRef)
    },

    async onSubmit() {
      if (this.$wait.is('creating car offer')) {
        return
      }

      this.$wait.start('creating car offer')
      this.resetErrors()
      const [error, response] = await createOffer(this.car.id, {
        ...this.form,
        item_fullsizeimages: this.form.item_fullsizeimages ? 'Y' : 'N'
      })
      this.$wait.end('creating car offer')

      if (error) {
        this.handleBackendValidationErrors(error)
        window.eventBus.$emit('mia-error', {
          error: ['Kundenangebot konnte nicht erstellt werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Kundenangebot erfolgreich erstellt']
      })
      this.$emit('done')
      window.open(`${process.env.VUE_APP_PROVIDER}/${response.data.url.replace(/^\//, '')}`)
    }
  }
}
</script>

<template>
  <mia-modal
    id="car-create-offer-modal"
    classes="v--modal flex flex-col"
    width="50%"
    height="auto"
    v-bind="$attrs"
    v-on="$listeners"
    @opened="onOpened"
  >
    <mia-modal-header>
      <h3 class="py-0 my-0">
        Kundenangebot erstellen
      </h3>
    </mia-modal-header>

    <div class="p-4">
      <mia-notice
        v-if="isMissingConsumptionValues"
        type="warning"
        permanent
        class="mb-3"
      >
        <div class="font-bold">
          Energieemissionswerte unvollständig
        </div>
        <div class="mt-1">
          Die Energieemissionswerte dieses Fahrzeugs sind nicht vollständig. Das Angebot wird ohne Pkw-EnVKV Label erzeugt.
        </div>
      </mia-notice>
      <mia-notice
        v-if="isMissingWltpValues"
        type="warning"
        permanent
        class="mb-3"
      >
        <div class="font-bold">
          WLTP unvollständig
        </div>
        <div class="mt-1">
          Die WLTP-Werte dieses Fahrzeugs sind nicht vollständig. Das Angebot wird ohne WLTP Datenblatt erzeugt.
        </div>
      </mia-notice>
      <mia-notice
        v-if="isMissingConfiguration"
        type="warning"
        permanent
        class="mb-3"
      >
        <div class="font-bold">
          Ausstattung unvollständig
        </div>
        <div class="mt-1">
          Zu diesem Fahrzeug sind aktuell noch keinerlei Ausstattungsmerkmale bekannt.
        </div>
      </mia-notice>
      <mia-notice
        v-if="isMissingImages"
        type="warning"
        permanent
        class="mb-3"
      >
        <div class="font-bold">
          Fahrzeugbilder unvollständig
        </div>
        <div class="mt-1">
          Zu diesem Fahrzeug sind aktuell noch keine Bilder verfügbar.
        </div>
      </mia-notice>

      <div class="grid grid-cols-2 gap-6">
        <div>
          <mia-feature-input
            ref="priceInputRef"
            v-model="form.item_price"
            :label="car.item_tax === 'differential' ? 'Angebotspreis inkl. Überführung' : 'Brutto Angebotspreis inkl. Überführung'"
            addon="€"
            input-class="text-right"
            :errors="getErrors('item_price')"
            @input="clearErrors('item_price')"
          />
        </div>
        <div>
          <template v-if="!isMissingImages">
            <mia-feature-toggle
              v-model="form.item_fullsizeimages"
              label="Große Fotos anhängen"
              label-position="top"
              :height="30"
              :width="75"
              input-style="margin-top: 3px"
              :errors="getErrors('item_fullsizeimages')"
              @input="clearErrors('item_fullsizeimages')"
            />
          </template>
        </div>
        <div class="col-span-2">
          <mia-feature-textarea
            v-model="form.item_offer_defaulttext"
            label="Angebotstext"
            :errors="getErrors('item_offer_defaulttext')"
            @input="clearErrors('item_offer_defaulttext')"
          />
        </div>
      </div>
    </div>

    <mia-modal-footer class="flex items-center justify-end">
      <mia-cancel-button
        label="Abbrechen"
        type="button"
        :disabled="$wait.any"
        size="small"
        @click="$emit('cancel')"
      />

      <mia-submit-button
        label="Kundenangebot erstellen"
        type="button"
        class="ml-3"
        :disabled="$wait.any"
        size="small"
        :is-loading="$wait.is('creating car offer')"
        @click="onSubmit"
      />
    </mia-modal-footer>
  </mia-modal>
</template>
